// pages/Community/SkoolMentions.jsx
//
// Every time someone mentioned us on Skool and the mention lane answered:
// who, which community, what they said, our reply and whether it went out —
// with that person's prospect state (booked / not interested / … decides what
// else we may say to them) and the override. What they said is recorded from
// 2026-09-26 and backfilled where Skool's notification history still had it.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import addNotification from "helpers/notify";
import { getSkoolMentions, setSkoolProspectState } from "api/connectors/connectors";
import { COLUMNS as PROSPECT_STATES } from "./SkoolProspects";

const PROSPECT = Object.fromEntries(PROSPECT_STATES.map((c) => [c.key, c]));
const SOURCE_LABEL = { manual: "set by you", booking: "booking", rule: "rule", ai: "AI" };
const STATUS = {
  success: { label: "Replied", fg: "#1a7f37", bg: "#e7f7ec" },
  queued: { label: "Queued", fg: "#9a6700", bg: "#fff4e5" },
  running: { label: "Sending", fg: "#9a6700", bg: "#fff4e5" },
  held: { label: "Awaiting approval", fg: "#0b3d91", bg: "#eef4ff" },
  failed: { label: "Failed", fg: "#b42318", bg: "#fdecea" },
  cancelled: { label: "Cancelled", fg: "#667085", bg: "#f2f3f5" },
};
const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : "—"; } catch (_) { return "—"; } };

function Pill({ label, fg, bg, title, testid }) {
  return <span data-testid={testid} title={title} style={{ fontSize: "0.68rem", fontWeight: 600, color: fg, background: bg, borderRadius: 10, padding: "1px 8px", whiteSpace: "nowrap" }}>{label}</span>;
}

export default function SkoolMentions({ brandId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState("");
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSkoolMentions(brandId ? { brandId } : {});
      setRows((res && res.mentions) || []);
    } catch (e) {
      addNotification(e?.message || e?.error || "Could not load Skool mentions", "error");
    } finally { setLoading(false); }
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    if (!filter) return rows;
    if (filter === "known") return rows.filter((r) => r.prospect);
    return rows.filter((r) => r.status === filter || (r.prospect && r.prospect.state === filter));
  }, [rows, filter]);

  const changeState = async (p, state) => {
    if (!p || !state) return;
    try {
      await setSkoolProspectState(p._id, state, state === "auto" ? "" : "set from Skool mentions");
      addNotification(state === "auto" ? "Handed back to the classifier" : `Now: ${(PROSPECT[state] || {}).label || state}`, "success");
      load();
    } catch (e) { addNotification(e?.error || e?.message || "Could not change the state", "error"); }
  };

  const replied = rows.filter((r) => r.status === "success").length;
  return (
    <div>
      <div className="d-flex align-items-center mb-2" style={{ gap: 10, flexWrap: "wrap" }}>
        <h6 className="mb-0">Skool mentions</h6>
        <span className="text-muted small">{rows.length} mentions · {replied} replied · {rows.filter((r) => r.prospect).length} from people we track</span>
        <select className="form-control form-control-sm ml-auto" style={{ width: 210 }} aria-label="Mentions filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All mentions</option>
          <option value="known">From people we track</option>
          {PROSPECT_STATES.map((c) => <option key={c.key} value={c.key}>Prospect: {c.label}</option>)}
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>Reply: {v.label}</option>)}
        </select>
        <button className="btn btn-link btn-sm p-0" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
      </div>

      {!shown.length && !loading && <div className="text-muted small">{rows.length ? "Nothing matches this filter." : "No mentions answered for this brand yet."}</div>}

      {shown.map((r) => {
        const st = STATUS[r.status] || STATUS.cancelled;
        const p = r.prospect;
        const pc = p ? (PROSPECT[p.state] || PROSPECT.new) : null;
        const isOpen = open === r._id;
        return (
          <div key={r._id} data-testid={`mention-${r._id}`} style={{ border: "1px solid #e4e7ec", borderRadius: 8, padding: "8px 10px", marginBottom: 8, background: "#fff" }}>
            <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap", cursor: "pointer" }} onClick={() => setOpen(isOpen ? "" : r._id)}>
              <b className="small">{r.author || "(someone)"}</b>
              {pc && <Pill testid="mention-prospect-pill" label={`${pc.label}${p.stateSource === "manual" ? " ✎" : ""}`} fg={pc.color} bg={pc.bg}
                title={`${pc.label} (${SOURCE_LABEL[p.stateSource] || p.stateSource})${p.reason ? ` — ${p.reason}` : ""}`} />}
              <span className="text-muted small">in {r.communityUrl ? <a href={r.communityUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{r.community}</a> : r.community}</span>
              <Pill label={st.label} fg={st.fg} bg={st.bg} title={r.error || ""} />
              <span className="text-muted small ml-auto">{fmt(r.mentionedAt || r.createdAt)}</span>
            </div>
            <div className="small" style={{ marginTop: 4, color: "#344054" }}>
              {r.mentionText
                ? <span>“{isOpen ? r.mentionText : `${r.mentionText.slice(0, 180)}${r.mentionText.length > 180 ? "…" : ""}`}”</span>
                : <span className="text-muted">(what they said was not recorded)</span>}
            </div>
            {isOpen && (
              <div style={{ marginTop: 6 }}>
                <div className="small" style={{ background: "#eef4ff", borderRadius: 8, padding: "6px 8px", whiteSpace: "pre-wrap" }}>
                  <b>Our reply:</b> {r.reply || "—"}
                </div>
                {r.postUrl && <a className="small" href={r.postUrl} target="_blank" rel="noreferrer">Open the thread</a>}
                {r.error && <div className="small" style={{ color: "#b42318" }}>{r.error}</div>}
                {pc ? (
                  <div data-testid="mention-prospect-block" style={{ marginTop: 6, padding: 8, borderRadius: 8, background: pc.bg, fontSize: "0.78rem" }}>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <b style={{ color: pc.color }}>{pc.label}</b>
                      <span className="text-muted">· {SOURCE_LABEL[p.stateSource] || p.stateSource}{p.handle ? ` · @${p.handle}` : ""}</span>
                      <select className="form-control form-control-sm ml-auto" style={{ width: 180 }} aria-label="Change prospect state" value=""
                        onChange={(e) => changeState(p, e.target.value)}>
                        <option value="">Change state…</option>
                        {PROSPECT_STATES.filter((x) => x.key !== p.state || p.stateSource !== "manual").map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                        {p.stateSource === "manual" && <option value="auto">Back to automatic</option>}
                      </select>
                    </div>
                    {p.reason && <div style={{ color: "#475467" }}>{p.reason}</div>}
                    {p.bookingAt && <div style={{ color: "#6a3fb5" }}>Call booked: {fmt(p.bookingAt)}</div>}
                    <div className="text-muted">{pc.hint}</div>
                  </div>
                ) : (
                  <div className="text-muted small" style={{ marginTop: 6 }}>
                    {r.userId ? "Not someone we track yet — prospect states cover members of our own community." : "No identity recorded for this mention (before 2026-09-26)."}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
